// frontend\src\pages\Chemicals.js
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  BiPlus,
  BiPlusCircle,
  BiTrash,
  BiEdit,
  BiMinus,
  BiCheck,
  BiX,
  BiSearch,
  BiFilter,
  BiChevronDown,
  BiChevronUp,
  BiReset,
  BiError,
  BiErrorAlt,
  BiTime,
  BiSolidFlask,
  BiMoney,
  BiPrinter,
} from "react-icons/bi";
import api from "../api/api";
import { CHEMICALS_API, CHEMICAL_TYPES_API} from "../constants";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import ConfirmModal from "../components/ConfirmModal";
import ScrollToTopButton from "../components/ScrollToTopButton";
import { useFileExport } from "../hooks/useFileExport";

const Chemicals = () => {
  const [chemicals, setChemicals] = useState([]);
  const [chemicalTypes, setChemicalTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showUseConfirm, setShowUseConfirm] = useState(null);
  const [useAmount, setUseAmount] = useState("");
  const [useProcess, setUseProcess] = useState("");
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    chemical_type: "",
    state: "",
    accounting_type: "",
    is_working_solution: "",
    expiry_status: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { isExporting, exportProgress, exportFile } = useFileExport();

  // Функция для применения фильтров
  const applyFiltersLocal = useCallback((chemicalsData, filtersToApply = appliedFilters) => {
    let filteredChemicals = [...chemicalsData];

    // Если фильтры пустые - возвращаем все данные
    if (Object.keys(filtersToApply).length === 0) {
      return filteredChemicals;
    }

    if (filtersToApply.searchTerm) {
      filteredChemicals = filteredChemicals.filter((chemical) =>
        chemical.name.toLowerCase().includes(filtersToApply.searchTerm.toLowerCase()) ||
        (chemical.brand && chemical.brand.toLowerCase().includes(filtersToApply.searchTerm.toLowerCase()))
      );
    }

    if (filtersToApply.chemical_type) {
      filteredChemicals = filteredChemicals.filter(
        (chemical) => String(chemical.chemical_type) === String(filtersToApply.chemical_type)
      );
    }

    if (filtersToApply.state) {
      filteredChemicals = filteredChemicals.filter(
        (chemical) => chemical.state === filtersToApply.state
      );
    }

    if (filtersToApply.accounting_type) {
      filteredChemicals = filteredChemicals.filter(
        (chemical) => chemical.accounting_type === filtersToApply.accounting_type
      );
    }

    if (filtersToApply.is_working_solution !== "") {
      filteredChemicals = filteredChemicals.filter(
        (chemical) => chemical.is_working_solution === (filtersToApply.is_working_solution === "true")
      );
    }

    if (filtersToApply.expiry_status) {
      const now = new Date();
      switch (filtersToApply.expiry_status) {
        case "expired":
          filteredChemicals = filteredChemicals.filter(
            (chemical) => chemical.expiry_date && new Date(chemical.expiry_date) < now
          );
          break;
        case "expiring_soon":
          const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
          filteredChemicals = filteredChemicals.filter(
            (chemical) => 
              chemical.expiry_date && 
              new Date(chemical.expiry_date) >= now &&
              new Date(chemical.expiry_date) <= thirtyDaysFromNow
          );
          break;
        case "not_expired":
          filteredChemicals = filteredChemicals.filter(
            (chemical) => 
              !chemical.expiry_date || 
              new Date(chemical.expiry_date) >= now
          );
          break;
        default:
          break;
      }
    }

    return filteredChemicals;
  }, [appliedFilters]);

  // Получаем отфильтрованные химикаты
  const filteredChemicals = applyFiltersLocal(chemicals);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log("Загрузка химикатов...");
      
      const [chemicalsResponse, typesResponse] = await Promise.all([
        api.get(CHEMICALS_API),
        api.get(CHEMICAL_TYPES_API)
      ]);
      
      const chemicalsData = Array.isArray(chemicalsResponse.data) ? chemicalsResponse.data : [];
      const typesData = Array.isArray(typesResponse.data) ? typesResponse.data : [];
      
      setChemicalTypes(typesData);
      setChemicals(chemicalsData);
      
      console.log("Химикаты загружены:", chemicalsData.length, "шт.");
      console.log("Типы химикатов загружены:", typesData.length, "шт.");
    } catch (error) {
      console.error("Ошибка загрузки:", error);
      toast.error("Ошибка загрузки данных!");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log("Первоначальная загрузка данных");
    loadData();
  }, [loadData]);

  useEffect(() => {
    console.log("Проверка состояния location:", location.state);
    if (location.state && location.state.shouldRefresh) {
      console.log("Обновление данных по shouldRefresh");
      loadData();
      navigate(location.pathname, { state: {} });
    }
  }, [location, navigate, loadData]);

  const applyFilters = () => {
    const newAppliedFilters = {
      ...(searchTerm && { searchTerm }),
      ...filters,
    };
    
    console.log("Применяем фильтры:", newAppliedFilters);
    setAppliedFilters(newAppliedFilters);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilters({
      chemical_type: "",
      state: "",
      accounting_type: "",
      is_working_solution: "",
      expiry_status: "",
    });
    setAppliedFilters({});
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      applyFilters();
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`${CHEMICALS_API}${id}/`);
      // Обновляем только основной стейт
      const newChemicals = chemicals.filter((chemical) => chemical.id !== id);
      setChemicals(newChemicals);
      setShowDeleteConfirm(null);
      toast.success("Химикат удален!");
    } catch (error) {
      toast.error("Ошибка удаления!");
    }
  };

  const handleUseChemical = async (chemical) => {
    // Для ресурсного учета не требуется ввод количества
    if (chemical.accounting_type === 'resource') {
      try {
        const response = await api.post(`${CHEMICALS_API}${chemical.id}/use_chemical/`, {
          process_type: useProcess,
        });

        if (response.status === 200) {
          if (response.data && response.data.status === "fully_used") {
            // Если химикат полностью использован, удаляем его из списка
            const newChemicals = chemicals.filter((chem) => chem.id !== chemical.id);
            setChemicals(newChemicals);
            toast.success("Химикат полностью использован и удален!");
          } else {
            // Обычное обновление - перезагружаем данные полностью
            await loadData();
            toast.success("Использование записано!");
          }
          setShowUseConfirm(null);
          setUseAmount("");
          setUseProcess("");
        }
      } catch (error) {
        if (error.response?.data?.error) {
          toast.error(error.response.data.error);
        } else {
          toast.error("Ошибка списания!");
        }
      }
    } else {
      // Объемный учет - требуется ввод количества
      if (!useAmount || parseFloat(useAmount) <= 0) {
        toast.error("Введите корректное количество!");
        return;
      }

      try {
        const response = await api.post(`${CHEMICALS_API}${chemical.id}/use_chemical/`, {
          amount_used: parseFloat(useAmount),
          process_type: useProcess,
        });

        if (response.status === 200) {
          if (response.data && response.data.status === "fully_used") {
            // Если химикат полностью использован, удаляем его из списка
            const newChemicals = chemicals.filter((chem) => chem.id !== chemical.id);
            setChemicals(newChemicals);
            toast.success("Химикат полностью использован и удален!");
          } else {
            // Обычное обновление - перезагружаем данные полностью
            await loadData();
            toast.success("Использование записано!");
          }
          setShowUseConfirm(null);
          setUseAmount("");
          setUseProcess("");
        }
      } catch (error) {
        if (error.response?.data?.error) {
          toast.error(error.response.data.error);
        } else {
          toast.error("Ошибка списания!");
        }
      }
    }
  };

  const handleExportPDF = () => {
    const chemicalsToExport = filteredChemicals.length > 0 ? filteredChemicals : chemicals;
    if (chemicalsToExport.length === 0) {
      toast.info("Нет данных для экспорта");
      return;
    }
    
    exportFile(
      (options) => api.get(`${CHEMICALS_API}export_pdf/`, options),
      "my_chemicals_catalog.pdf",
      "Каталог химикатов успешно экспортирован!"
    );
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    return new Date(expiryDate) <= thirtyDaysFromNow && new Date(expiryDate) >= new Date();
  };

  // Функция для проверки, заканчивается ли химикат
  const isLowStock = (chemical) => {
    if (chemical.is_low_stock !== undefined) {
      return chemical.is_low_stock;
    }
    
    if (chemical.accounting_type === 'volume') {
      if (chemical.initial_quantity && chemical.initial_quantity > 0) {
        const percentage = (chemical.quantity / chemical.initial_quantity) * 100;
        return percentage < 15;
      }
    } else {
      if (chemical.max_uses > 0) {
        const percentage = ((chemical.max_uses - chemical.current_uses) / chemical.max_uses) * 100;
        return percentage < 15;
      }
    }
    return false;
  };

  const formatQuantity = (chemical) => {
    return `${chemical.quantity} ${chemical.unit === 'ml' ? 'мл' : chemical.unit === 'l' ? 'л' : chemical.unit === 'g' ? 'г' : 'кг'}`;
  };

  // Функция для расчета процента остатка
  const calculateRemainingPercentage = (chemical) => {
    if (chemical.remaining_percentage !== undefined && chemical.remaining_percentage !== null) {
      return parseFloat(chemical.remaining_percentage);
    }
    
    if (chemical.accounting_type === 'volume') {
      if (chemical.initial_quantity && chemical.initial_quantity > 0) {
        const initialQty = parseFloat(chemical.initial_quantity);
        const currentQty = parseFloat(chemical.quantity);
        if (initialQty > 0) {
          return (currentQty / initialQty) * 100;
        }
      }
    } else {
      if (chemical.max_uses > 0) {
        return ((chemical.max_uses - chemical.current_uses) / chemical.max_uses) * 100;
      }
    }
    return 0;
  };

  const ChemicalDashboard = () => {
    // Используем filteredChemicals если есть фильтры, иначе chemicals
    const displayChemicals = filteredChemicals.length > 0 ? filteredChemicals : chemicals;
    
    // Группируем химикаты по типам
    const typeStats = displayChemicals.reduce((accumulator, chemical) => {
      const typeId = chemical.chemical_type;
      const typeName = chemical.chemical_type_name || "Без типа";
      
      if (!accumulator[typeId]) {
        accumulator[typeId] = {
          name: typeName,
          total: 0,
          volume: 0,
          resource: 0,
          working: 0,
          concentrate: 0,
          totalQuantity: 0,
          totalInitialQuantity: 0,
          totalCost: 0,
          items: [],
        };
      }
      
      accumulator[typeId].total += 1;
      accumulator[typeId].totalQuantity += parseFloat(chemical.quantity);
      accumulator[typeId].totalInitialQuantity += parseFloat(chemical.initial_quantity || chemical.quantity);
      
      if (chemical.accounting_type === 'volume') {
        accumulator[typeId].volume += 1;
      } else {
        accumulator[typeId].resource += 1;
      }
      
      if (chemical.is_working_solution) {
        accumulator[typeId].working += 1;
      } else {
        accumulator[typeId].concentrate += 1;
      }
      
      if (chemical.price) {
        accumulator[typeId].totalCost += parseFloat(chemical.price);
      }
      
      accumulator[typeId].items.push(chemical);
      return accumulator;
    }, {});

    // Рассчитываем количество карточек для определения размера колонок
    const typeCount = Object.keys(typeStats).length;
    const cardClass =
      typeCount <= 2
        ? "col-md-6 col-6"
        : typeCount === 3
        ? "col-md-4 col-6"
        : "col-md-3 col-6";

    const expiredChemicals = displayChemicals.filter((chemical) => isExpired(chemical.expiry_date));
    const expiringSoon = displayChemicals.filter((chemical) => isExpiringSoon(chemical.expiry_date));
    const lowStock = displayChemicals.filter((chemical) => isLowStock(chemical));
    const cannotBeUsed = displayChemicals.filter((chemical) => !chemical.can_be_used || isExpired(chemical.expiry_date));

    // Общая стоимость всех химикатов
    const totalInvestment = displayChemicals.reduce((sum, chemical) => {
      return chemical.price ? sum + parseFloat(chemical.price) : sum;
    }, 0);

    // Общий процент использования
    const totalUsagePercentage = displayChemicals.length > 0 
      ? displayChemicals.reduce((sum, chemical) => sum + calculateRemainingPercentage(chemical), 0) / displayChemicals.length
      : 0;

    const renderChemicalStats = (data) => {
      const totalInitial = parseFloat(data.totalInitialQuantity);
      const totalCurrent = parseFloat(data.totalQuantity);
      const usagePercentage = totalInitial > 0 
        ? (totalCurrent / totalInitial) * 100 
        : 0;

      return (
        <div className="text-center">
          <div className="mb-1">
            <span className="me-1">Всего:</span>
            <span style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
              {data.total}
            </span>
            <span className="ms-1">шт.</span>
          </div>
          {data.volume > 0 && (
            <div className="mb-1">
              <span className="me-1">Объемные:</span>
              <span style={{ fontSize: "1.1rem", fontWeight: "bold", color: "var(--info)" }}>
                {data.volume}
              </span>
              <span className="ms-1">шт.</span>
            </div>
          )}
          {data.resource > 0 && (
            <div className="mb-1">
              <span className="me-1">Ресурсные:</span>
              <span style={{ fontSize: "1.1rem", fontWeight: "bold", color: "var(--warning)" }}>
                {data.resource}
              </span>
              <span className="ms-1">шт.</span>
            </div>
          )}
          <div className="small text-muted mt-2">
            Средний остаток: {usagePercentage.toFixed(1)}%
          </div>
          <div className="progress mt-1" style={{ height: "6px" }}>
            <div 
              className={`progress-bar ${usagePercentage < 15 ? 'bg-danger' : usagePercentage < 30 ? 'bg-warning' : 'bg-success'}`}
              role="progressbar" 
              style={{ width: `${usagePercentage}%` }}
              aria-valuenow={usagePercentage}
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>
        </div>
      );
    };

    return (
      <div className="dashboard mb-4">
        <div className="alert alert-info d-flex align-items-center mb-4">
          <BiMoney className="me-2" size="1.5em" />
          <div>
            Найдено <strong>{displayChemicals.length}</strong> химикат
            {displayChemicals.length === 1 ? "" : displayChemicals.length >= 2 && displayChemicals.length <= 4 ? "а" : "ов"}
            {totalInvestment > 0 && (
              <> на сумму:{" "}
                <strong>
                  {totalInvestment.toLocaleString("ru-RU", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                </strong>
                руб
              </>
            )}
            {displayChemicals.length > 0 && (
              <> · Средний остаток: <strong>{totalUsagePercentage.toFixed(1)}%</strong></>
            )}
          </div>
        </div>

        <div className="row g-3 mb-4">
          {Object.entries(typeStats).map(([typeId, data]) => (
            <div className={cardClass} key={typeId}>
              <div
                className={`card h-100 ${
                  data.total <= 3 ? "border-warning" : ""
                }`}
              >
                <div className="card-body p-2 p-sm-3">
                  <h5 className="card-title text-center mb-2" style={{ color: "var(--text-primary)" }}>
                    <BiSolidFlask className="me-2" />
                    {data.name}
                  </h5>

                  {renderChemicalStats(data)}

                  {data.totalCost > 0 && (
                    <div className="small text-muted text-center mt-2">
                      На сумму:{" "}
                      {data.totalCost.toLocaleString("ru-RU", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      руб
                    </div>
                  )}

                  {data.total <= 3 && (
                    <div className="text-center mt-2">
                      <span className="badge bg-warning text-dark">
                        <BiError className="me-1" />
                        {data.total === 1 ? "Последний!" : "Мало!"}
                      </span>
                    </div>
                  )}

                  <div className="text-center mt-2">
                    <Link
                      to={{
                        pathname: "/chemicals/new",
                        search: `?chemical_type=${encodeURIComponent(typeId)}`,
                      }}
                      className="small"
                      style={{ color: "var(--btn-primary-bg)" }}
                    >
                      <BiPlus className="me-1" />
                      Добавить
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {(expiredChemicals.length > 0 || expiringSoon.length > 0 || lowStock.length > 0 || cannotBeUsed.length > 0) && (
          <div className="row g-3 align-items-stretch">
            {lowStock.length > 0 && (
              <div className="col-lg-4 d-flex">
                <div className="alert alert-warning py-2 w-100 d-flex flex-column" style={{ minHeight: '120px' }}>
                  <h5 className="d-flex align-items-center mb-2">
                    <BiErrorAlt className="me-2" />
                    Заканчиваются:
                  </h5>
                  <div className="d-flex flex-wrap gap-1 align-items-start">
                    {lowStock.map((chemical) => {
                      const percentage = calculateRemainingPercentage(chemical);
                      return (
                        <span key={chemical.id} className="badge bg-light text-dark">
                          {chemical.name} ({typeof percentage === 'number' ? percentage.toFixed(1) : '0.0'}%)
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {expiringSoon.length > 0 && (
              <div className="col-lg-4 d-flex">
                <div className="alert alert-danger py-2 w-100 d-flex flex-column" style={{ minHeight: '120px' }}>
                  <h5 className="d-flex align-items-center mb-2">
                    <BiTime className="me-2" />
                    Срок годности:
                  </h5>
                  <div className="d-flex flex-wrap gap-1 align-items-start">
                    {expiringSoon.map((chemical) => (
                      <span key={chemical.id} className="badge bg-light text-dark">
                        {chemical.name} до{" "}
                        {new Date(chemical.expiry_date).toLocaleDateString("ru-RU")}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {cannotBeUsed.length > 0 && (
              <div className="col-lg-4 d-flex">
                <div className="alert alert-dark py-2 w-100 d-flex flex-column" style={{ minHeight: '120px' }}>
                  <h5 className="d-flex align-items-center mb-2">
                    <BiError className="me-2" />
                    Исчерпан ресурс:
                  </h5>
                  <div className="d-flex flex-wrap gap-1 align-items-start">
                    {cannotBeUsed.map((chemical) => (
                      <span key={chemical.id} className="badge bg-light text-dark">
                        {chemical.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  const hasActiveFilters = Object.keys(appliedFilters).length > 0;
  
  // Определяем какие данные показывать в таблице
  const displayChemicalsInTable = hasActiveFilters ? filteredChemicals : chemicals;

  return (
    <div className="container-fluid mt-4">
      {/* === ДЕСКТОП: Полные кнопки === */}
      <div className="d-none d-lg-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0" style={{ color: "var(--text-primary)" }}>Моя фотохимия</h1>
        <div className="d-flex gap-2">
          <button
            onClick={handleExportPDF}
            className="btn btn-outline-primary"
            disabled={chemicals.length === 0 || isExporting}
          >
            {isExporting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Генерация ({exportProgress}%)
              </>
            ) : (
              <>
                <BiPrinter className="me-2" />
                Экспорт в PDF
              </>
            )}
          </button>
          <Link to="/chemicals/new" className="btn btn-primary">
            <BiPlusCircle className="me-2" />
            Добавить химикат
          </Link>
        </div>
      </div>

      {/* === МОБИЛЬНЫЙ: Компактные кнопки под заголовком === */}
      <div className="d-lg-none d-flex flex-column gap-3 mb-4">
        <h1 className="h4 mb-0" style={{ color: "var(--text-primary)" }}>Моя фотохимия</h1>
        <div className="d-flex gap-2">
          <button
            onClick={handleExportPDF}
            className="btn btn-outline-primary btn-sm flex-fill"
            disabled={chemicals.length === 0 || isExporting}
          >
            {isExporting ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              "Экспорт"
            )}
          </button>
          <Link to="/chemicals/new" className="btn btn-primary btn-sm flex-fill">
            Новая
          </Link>
        </div>
      </div>

      {isExporting && (
        <div className="progress mb-3">
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            style={{ width: `${exportProgress}%` }}
            aria-valuenow={exportProgress}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {exportProgress}%
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <button
          className="btn btn-outline-primary d-flex align-items-center"
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
          type="button"
        >
          <BiFilter className="me-2" />
          Фильтры
          {isFiltersExpanded ? (
            <BiChevronUp className="ms-2" />
          ) : (
            <BiChevronDown className="ms-2" />
          )}
        </button>

        {!isFiltersExpanded && hasActiveFilters && (
          <div className="d-flex flex-wrap gap-2">
            {appliedFilters.searchTerm && (
              <span className="badge bg-primary">Поиск: {appliedFilters.searchTerm}</span>
            )}
            {appliedFilters.chemical_type && (
              <span className="badge bg-secondary">
                Тип: {chemicalTypes.find(t => String(t.id) === String(appliedFilters.chemical_type))?.name}
              </span>
            )}
            {appliedFilters.state && (
              <span className="badge bg-info text-dark">
                Состояние: {appliedFilters.state === 'powder' ? 'Порошок' : appliedFilters.state === 'liquid' ? 'Жидкость' : 'Концентрат'}
              </span>
            )}
            {appliedFilters.accounting_type && (
              <span className="badge bg-warning text-dark">
                Учет: {appliedFilters.accounting_type === 'volume' ? 'Объемный' : 'Ресурсный'}
              </span>
            )}
            {appliedFilters.is_working_solution !== "" && (
              <span className="badge bg-success">
                {appliedFilters.is_working_solution === "true" ? "Рабочий раствор" : "Концентрат"}
              </span>
            )}
            {appliedFilters.expiry_status && (
              <span className="badge bg-danger">
                Срок: {appliedFilters.expiry_status === 'expired' ? 'Просрочен' : appliedFilters.expiry_status === 'expiring_soon' ? 'Скоро истекает' : 'Не истек'}
              </span>
            )}
          </div>
        )}
      </div>

      <div className={`card mb-4 ${isFiltersExpanded ? "" : "d-none"}`}>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Поиск</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Поиск по названию или производителю..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </div>

            <div className="col-md-2">
              <label className="form-label">Тип химиката</label>
              <select
                className="form-select"
                name="chemical_type"
                value={filters.chemical_type}
                onChange={handleFilterChange}
              >
                <option value="">Все типы</option>
                {chemicalTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Состояние</label>
              <select
                className="form-select"
                name="state"
                value={filters.state}
                onChange={handleFilterChange}
              >
                <option value="">Все</option>
                <option value="powder">Порошок</option>
                <option value="liquid">Жидкость</option>
                <option value="concentrate">Концентрат</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Тип учета</label>
              <select
                className="form-select"
                name="accounting_type"
                value={filters.accounting_type}
                onChange={handleFilterChange}
              >
                <option value="">Все</option>
                <option value="volume">Объемный</option>
                <option value="resource">Ресурсный</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Тип раствора</label>
              <select
                className="form-select"
                name="is_working_solution"
                value={filters.is_working_solution}
                onChange={handleFilterChange}
              >
                <option value="">Все</option>
                <option value="false">Концентрат</option>
                <option value="true">Рабочий раствор</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Срок годности</label>
              <select
                className="form-select"
                name="expiry_status"
                value={filters.expiry_status}
                onChange={handleFilterChange}
              >
                <option value="">Все</option>
                <option value="expired">Просроченные</option>
                <option value="expiring_soon">Скоро истекают</option>
                <option value="not_expired">Не истекшие</option>
              </select>
            </div>

            <div className="col-12 d-flex justify-content-end gap-2">
              <div className="d-flex gap-2">
                <button className="btn btn-primary" onClick={applyFilters}>
                  <BiSearch className="me-2" />
                  Применить
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={resetFilters}
                  title="Сбросить фильтры"
                >
                  <BiReset />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {chemicals.length > 0 && <ChemicalDashboard />}

      {/* === ДЕСКТОП: ТАБЛИЦА === */}
      <div className="d-none d-lg-block">
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>№</th>
                <th>Название</th>
                <th>Производитель</th>
                <th>Тип</th>
                <th>Учет</th>
                <th>Состояние</th>
                <th>Количество</th>
                <th>Остаток</th>
                <th>Тип раствора</th>
                <th>Срок годности</th>
                <th>Место хранения</th>
                <th style={{ width: "140px" }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {displayChemicalsInTable.length > 0 ? (
                displayChemicalsInTable.map((chemical, index) => {
                  const percentage = calculateRemainingPercentage(chemical);
                  const isLowStockItem = isLowStock(chemical);
                  const percentageNumber = typeof percentage === 'number' ? percentage : 0;
                  const isExpiredItem = isExpired(chemical.expiry_date);
                  const cannotBeUsed = !chemical.can_be_used || isExpiredItem;
                  
                  return (
                    <tr key={chemical.id}>
                      <td className="text-center fw-bold">{index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          {chemical.name}
                          {cannotBeUsed && (
                            <BiError className="ms-1 text-danger" size="2em" title="Нельзя использовать" />
                          )}
                        </div>
                      </td>
                      <td>{chemical.brand || "-"}</td>
                      <td>{chemical.chemical_type_name}</td>
                      <td>
                        <span className={`badge ${
                          chemical.accounting_type === 'volume' ? 'bg-info' : 'bg-warning text-dark'
                        }`}>
                          {chemical.accounting_type === 'volume' ? 'Объем' : 'Ресурс'}
                        </span>
                      </td>
                      <td>
                        {chemical.state === "powder" ? (
                          <span className="badge bg-secondary">Порошок</span>
                        ) : chemical.state === "liquid" ? (
                          <span className="badge bg-primary">Жидкость</span>
                        ) : (
                          <span className="badge bg-info text-dark">Концентрат</span>
                        )}
                      </td>
                      <td>
                        {chemical.accounting_type === 'volume' ? (
                          <>
                            {formatQuantity(chemical)}
                            {chemical.initial_quantity > 0 && (
                              <div className="small text-muted">
                                из {chemical.initial_quantity} {chemical.unit === 'ml' ? 'мл' : chemical.unit === 'l' ? 'л' : chemical.unit === 'g' ? 'г' : 'кг'}
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div>{chemical.current_uses} из {chemical.max_uses} использований</div>
                            {chemical.manufacturer_recommendation && (
                              <div className="small text-muted">
                                рек.: {chemical.manufacturer_recommendation}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="me-2">{percentageNumber.toFixed(1)}%</div>
                          <div className="progress flex-grow-1" style={{ height: "8px", width: "60px" }}>
                            <div 
                              className={`progress-bar ${
                                cannotBeUsed ? 'bg-danger' : 
                                isLowStockItem ? 'bg-warning' : 'bg-success'
                              }`}
                              role="progressbar" 
                              style={{ width: `${Math.min(percentageNumber, 100)}%` }}
                            ></div>
                          </div>
                          {isLowStockItem && !cannotBeUsed && (
                            <BiError className="ms-1 text-warning" title="Заканчивается" />
                          )}
                        </div>
                      </td>
                      <td>
                        {chemical.is_working_solution ? (
                          <span className="badge bg-success">Рабочий</span>
                        ) : (
                          <span className="badge bg-warning text-dark">Концентрат</span>
                        )}
                      </td>
                      <td
                        className={
                          isExpiredItem
                            ? "text-danger fw-bold"
                            : isExpiringSoon(chemical.expiry_date)
                            ? "text-warning"
                            : ""
                        }
                      >
                        {chemical.expiry_date
                          ? new Date(chemical.expiry_date).toLocaleDateString("ru-RU")
                          : "-"}                      
                      </td>
                      <td>{chemical.storage_location || "-"}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={() => setShowUseConfirm(chemical)}
                            disabled={cannotBeUsed}
                            title={cannotBeUsed ? "Нельзя использовать" : "Использовать химикат"}
                          >
                            <BiMinus />
                          </button>
                          <Link
                            to={`/chemicals/edit/${chemical.id}`}
                            className="btn btn-sm btn-outline-primary"
                            title="Редактировать"
                          >
                            <BiEdit />
                          </Link>

                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => setShowDeleteConfirm(chemical.id)}
                            title="Удалить"
                          >
                            <BiTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="12" className="text-center py-4">
                    <div className="alert alert-info mb-0">
                      {hasActiveFilters ? (
                        "Химикаты по заданным фильтрам не найдены"
                      ) : (
                        <>
                          У вас пока нет химикатов.{" "}
                          <Link to="/chemicals/new" className="alert-link">
                            Добавьте первый
                          </Link>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* === МОБИЛЬНЫЕ: КАРТОЧКИ === */}
      <div className="d-lg-none">
        {displayChemicalsInTable.length > 0 ? (
          displayChemicalsInTable.map((chemical, index) => {
            const percentage = calculateRemainingPercentage(chemical);
            const isLowStockItem = isLowStock(chemical);
            const percentageNumber = typeof percentage === 'number' ? percentage : 0;
            const isExpiredItem = isExpired(chemical.expiry_date);
            const cannotBeUsed = !chemical.can_be_used || isExpiredItem;

            return (
              <div key={chemical.id} className="card mb-3">
                <div className="card-body">
                  <h6 className="card-title mb-2">
                    <span className="fw-bold me-2">{index + 1}.</span>
                    {chemical.name}
                    {cannotBeUsed && (
                      <BiError className="ms-1 text-danger" size="1.2em" title="Нельзя использовать" />
                    )}
                  </h6>

                  <div className="small text-muted mb-1">
                    <strong>Производитель:</strong> {chemical.brand || "-"}
                  </div>

                  <div className="small text-muted mb-1">
                    <strong>Тип:</strong> {chemical.chemical_type_name}
                  </div>

                  <div className="small text-muted mb-1 d-flex gap-2">
                    <div>
                      <strong>Учет:</strong>{" "}
                      <span className={`badge ${chemical.accounting_type === 'volume' ? 'bg-info' : 'bg-warning text-dark'}`}>
                        {chemical.accounting_type === 'volume' ? 'Объем' : 'Ресурс'}
                      </span>
                    </div>
                    <div>
                      <strong>Состояние:</strong>{" "}
                      {chemical.state === "powder" ? (
                        <span className="badge bg-secondary">Порошок</span>
                      ) : chemical.state === "liquid" ? (
                        <span className="badge bg-primary">Жидкость</span>
                      ) : (
                        <span className="badge bg-info text-dark">Концентрат</span>
                      )}
                    </div>
                  </div>

                  <div className="small text-muted mb-1">
                    <strong>Количество:</strong>{" "}
                    {chemical.accounting_type === 'volume' ? (
                      <>
                        {formatQuantity(chemical)}
                        {chemical.initial_quantity > 0 && (
                          <span className="text-muted ms-1">
                            (из {chemical.initial_quantity} {chemical.unit === 'ml' ? 'мл' : chemical.unit === 'l' ? 'л' : chemical.unit === 'g' ? 'г' : 'кг'})
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        {chemical.current_uses} из {chemical.max_uses} использований
                        {chemical.manufacturer_recommendation && (
                          <span className="text-muted ms-1">
                            (рек.: {chemical.manufacturer_recommendation})
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  <div className="small text-muted mb-1 d-flex align-items-center gap-2">
                    <strong>Остаток:</strong>
                    <span>{percentageNumber.toFixed(1)}%</span>
                    <div className="progress flex-grow-1" style={{ height: "6px", width: "80px" }}>
                      <div 
                        className={`progress-bar ${
                          cannotBeUsed ? 'bg-danger' : 
                          isLowStockItem ? 'bg-warning' : 'bg-success'
                        }`}
                        style={{ width: `${Math.min(percentageNumber, 100)}%` }}
                      ></div>
                    </div>
                    {isLowStockItem && !cannotBeUsed && (
                      <BiError className="text-warning" title="Заканчивается" />
                    )}
                  </div>

                  <div className="small text-muted mb-1">
                    <strong>Тип раствора:</strong>{" "}
                    {chemical.is_working_solution ? (
                      <span className="badge bg-success">Рабочий</span>
                    ) : (
                      <span className="badge bg-warning text-dark">Концентрат</span>
                    )}
                  </div>

                  <div className={`small mb-1 ${isExpiredItem ? 'text-danger fw-bold' : isExpiringSoon(chemical.expiry_date) ? 'text-warning' : ''}`}>
                    <strong>Срок годности:</strong>{" "}
                    {chemical.expiry_date
                      ? new Date(chemical.expiry_date).toLocaleDateString("ru-RU")
                      : "-"}
                  </div>

                  <div className="small text-muted mb-2">
                    <strong>Место хранения:</strong> {chemical.storage_location || "-"}
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => setShowUseConfirm(chemical)}
                      disabled={cannotBeUsed}
                      title={cannotBeUsed ? "Нельзя использовать" : "Использовать химикат"}
                    >
                      <BiMinus />
                    </button>
                    <Link
                      to={`/chemicals/edit/${chemical.id}`}
                      className="btn btn-sm btn-outline-primary"
                      title="Редактировать"
                    >
                      <BiEdit />
                    </Link>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => setShowDeleteConfirm(chemical.id)}
                      title="Удалить"
                    >
                      <BiTrash />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="alert alert-info">
            {hasActiveFilters ? (
              "Химикаты по заданным фильтрам не найдены"
            ) : (
              <>
                У вас пока нет химикатов.{" "}
                <Link to="/chemicals/new" className="alert-link">
                  Добавьте первый
                </Link>
              </>
            )}
          </div>
        )}
      </div>

      <ScrollToTopButton/>

      {/* Модальное окно использования химиката */}
      {showUseConfirm && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Использование химиката</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowUseConfirm(null);
                    setUseAmount("");
                    setUseProcess("");
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">
                    Химикат: <strong>{showUseConfirm.name}</strong>
                  </label>
                </div>
                
                {showUseConfirm.accounting_type === 'volume' ? (
                  <>
                    <div className="mb-3">
                      <label className="form-label">
                        Доступно:{" "}
                        <strong>
                          {showUseConfirm.quantity}{" "}
                          {showUseConfirm.unit === "ml" ? "мл" : showUseConfirm.unit === "l" ? "л" : showUseConfirm.unit === "g" ? "г" : "кг"}
                        </strong>
                        {showUseConfirm.initial_quantity > 0 && (
                          <span className="text-muted ms-2">
                            ({calculateRemainingPercentage(showUseConfirm).toFixed(1)}% от исходного)
                          </span>
                        )}
                      </label>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="useAmount" className="form-label">
                        Количество использовано *
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        id="useAmount"
                        value={useAmount}
                        onChange={(e) => setUseAmount(e.target.value)}
                        placeholder="Введите количество"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-3">
                      <label className="form-label">
                        Использовано:{" "}
                        <strong>
                          {showUseConfirm.current_uses} из {showUseConfirm.max_uses}
                        </strong>
                        <span className="text-muted ms-2">
                          ({calculateRemainingPercentage(showUseConfirm).toFixed(1)}% осталось)
                        </span>
                      </label>
                    </div>
                    <div className="alert alert-info">
                      <small>
                        Для ресурсного учета будет засчитано одно использование. 
                        Объем раствора уменьшится незначительно.
                      </small>
                    </div>
                  </>
                )}
                
                <div className="mb-3">
                  <label htmlFor="useProcess" className="form-label">
                    Тип процесса
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="useProcess"
                    value={useProcess}
                    onChange={(e) => setUseProcess(e.target.value)}
                    placeholder="Например: проявка пленки, фиксаж и т.д."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowUseConfirm(null);
                    setUseAmount("");
                    setUseProcess("");
                  }}
                >
                  <BiX className="me-1" /> Отмена
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleUseChemical(showUseConfirm)}
                  disabled={
                    showUseConfirm.accounting_type === 'volume' 
                      ? !useAmount || parseFloat(useAmount) <= 0
                      : false
                  }
                >
                  <BiCheck className="me-1" /> Подтвердить
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления */}
      <ConfirmModal
        isOpen={!!showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(null)}
        onConfirm={() => handleDelete(showDeleteConfirm)}
        title="Подтверждение удаления"
        message="Вы уверены, что хотите удалить этот химикат? Это действие нельзя отменить."
        confirmText="Удалить"
        cancelText="Отмена"
        type="delete"
      />
    </div>
  );
};

export default Chemicals;